/**
 * Program Milestone Configuration Component
 * Sponsor interface for creating programs with configurable milestone payouts
 * Integrates with payment infrastructure for CBO and member payouts
 * Trust Marketplace - Configurable Milestone System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { getWeaveinApiUrl } from '@/src/config';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  Plus,
  Trash2,
  DollarSign,
  Target,
  Calendar,
  Users,
  Award,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Sparkles
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface NewMilestoneFormData {
  activity_type: string;
  milestone_name: string;
  milestone_description: string;
  points_awarded: number;
  monetary_value: number;
  is_repeatable: boolean;
  is_active: boolean;
}

const ACTIVITY_TYPES = [
  'medicaid_enrollment',
  'health_wellness',
  'social_services',
  'transportation',
  'referral',
  'engagement',
  'housing',
  'employment',
  'education',
  'childcare',
  'other'
];

interface ProgramFormData {
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  enrollment_limit?: number;
  is_active: boolean;
}

interface MilestoneConfig {
  milestone_id: number;
  milestone_name: string;
  milestone_type: string;
  is_required: boolean;
  target_quantity: number;
  target_timeframe_days?: number;
  cbo_payout_amount: number;
  member_payout_amount: number;
  points_awarded: number;
  max_payouts_per_member?: number;
}

interface AvailableMilestone {
  id: number;
  activity_type: string; // Changed from milestone_code
  milestone_name: string;
  milestone_description: string | null; // Changed from description
  points_awarded: number;
  monetary_value: number;
  is_repeatable: boolean;
  is_active: boolean;
}

const ProgramMilestoneConfiguration: React.FC = () => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const [programData, setProgramData] = useState<ProgramFormData>({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    is_active: true
  });

  const [milestones, setMilestones] = useState<MilestoneConfig[]>([]);
  const [availableMilestones, setAvailableMilestones] = useState<AvailableMilestone[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingMilestones, setFetchingMilestones] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Create New Milestone Dialog
  const [showCreateMilestoneDialog, setShowCreateMilestoneDialog] = useState(false);
  const [createMilestoneLoading, setCreateMilestoneLoading] = useState(false);
  const [newMilestoneForm, setNewMilestoneForm] = useState<NewMilestoneFormData>({
    activity_type: '',
    milestone_name: '',
    milestone_description: '',
    points_awarded: 0,
    monetary_value: 0,
    is_repeatable: false,
    is_active: true
  });

  const handleCreateMilestone = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMilestoneForm.activity_type || !newMilestoneForm.milestone_name) {
      setError('Activity type and milestone name are required');
      return;
    }

    try {
      setCreateMilestoneLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const response = await authFetch(getWeaveinApiUrl('/api/member-activity-milestones/'), {
        method: 'POST',
        jsonBody: newMilestoneForm
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create milestone');
      }

      // Refresh milestones list
      await fetchAvailableMilestones();

      // Reset form and close dialog
      setNewMilestoneForm({
        activity_type: '',
        milestone_name: '',
        milestone_description: '',
        points_awarded: 0,
        monetary_value: 0,
        is_repeatable: false,
        is_active: true
      });
      setShowCreateMilestoneDialog(false);
    } catch (err) {
      console.error('Error creating milestone:', err);
      setError(err instanceof Error ? err.message : 'Failed to create milestone');
    } finally {
      setCreateMilestoneLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailableMilestones();
  }, []);

  const fetchAvailableMilestones = async () => {
    try {
      setFetchingMilestones(true);
      setError(null);
      const url = getWeaveinApiUrl('/api/member-activity-milestones/');  // Note: trailing slash required by FastAPI
      const token = localStorage.getItem('auth_token');

      const response = await authFetch(url);

      if (!response.ok) {
        // Try to get detailed error message
        let errorMessage = 'Failed to fetch available milestones';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // If JSON parsing fails, use status text
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setAvailableMilestones(data || []);
    } catch (err) {
      console.error('Error fetching milestones:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to load milestones';
      setError(`${errorMsg}. Please ensure the backend server is running and milestones are configured.`);
    } finally {
      setFetchingMilestones(false);
    }
  };

  const addMilestone = () => {
    const newMilestone: MilestoneConfig = {
      milestone_id: 0,
      milestone_name: '',
      milestone_type: '',
      is_required: false,
      target_quantity: 1,
      cbo_payout_amount: 0,
      member_payout_amount: 0,
      points_awarded: 0
    };
    setMilestones([...milestones, newMilestone]);
  };

  const removeMilestone = (index: number) => {
    setMilestones(milestones.filter((_, i) => i !== index));
  };

  const updateMilestone = (index: number, field: keyof MilestoneConfig, value: any) => {
    const updated = [...milestones];

    if (field === 'milestone_id') {
      const selected = availableMilestones.find(m => m.id === parseInt(value));
      if (selected) {
        updated[index] = {
          ...updated[index],
          milestone_id: selected.id,
          milestone_name: selected.milestone_name,
          milestone_type: selected.activity_type,
          target_quantity: 1, // Default to 1 since default_target_quantity doesn't exist
          points_awarded: selected.points_awarded,
          member_payout_amount: selected.monetary_value
        };
      }
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }

    setMilestones(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!programData.name || !programData.start_date || !programData.end_date) {
      setError('Please fill in all required program fields');
      return;
    }

    if (milestones.length === 0) {
      setError('Please add at least one milestone');
      return;
    }

    for (const milestone of milestones) {
      if (!milestone.milestone_id) {
        setError('Please select a milestone type for all milestones');
        return;
      }
      if (milestone.target_quantity <= 0) {
        setError('Target quantity must be greater than 0');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        program: programData,
        milestones: milestones
      };

      const url = getWeaveinApiUrl('/api/programs/with-milestones');
      const token = localStorage.getItem('auth_token');

      const response = await authFetch(url, {
        method: 'POST',
        jsonBody: payload
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Program creation error:', errorData);

        // Handle validation errors (FastAPI returns array of errors for 422)
        if (response.status === 422 && Array.isArray(errorData.detail)) {
          const errorMessages = errorData.detail.map((err: any) =>
            `${err.loc?.join(' → ') || 'Field'}: ${err.msg}`
          ).join('\n');
          throw new Error(`Validation errors:\n${errorMessages}`);
        }

        throw new Error(errorData.detail || 'Failed to create program');
      }

      const result = await response.json();
      console.log('Program created successfully:', result);

      setSuccess(true);

      // Reset form
      setProgramData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        is_active: true
      });
      setMilestones([]);

      setTimeout(() => setSuccess(false), 5000);

    } catch (err) {
      console.error('Error creating program:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalPayouts = () => {
    return milestones.reduce((sum, m) => sum + m.cbo_payout_amount + m.member_payout_amount, 0);
  };

  if (fetchingMilestones) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <LoadingSpinner />
        <p className="mt-4 text-gray-600">{t('incentives.loading_available_milestones', 'Loading available milestones...')}</p>
      </div>
    );
  }

  // Show error state with retry button
  if (error && availableMilestones.length === 0) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div>
          {!tabbed && (
          <h1 className="text-3xl font-bold text-gray-900">{t('incentives.configure_program_milestones', 'Configure Program Milestones')}</h1>
          )}
          <p className="text-sm text-gray-600 mt-2">
            {t('incentives.create_a_new_program_with', 'Create a new program with sponsor-configurable milestone payouts')}
          </p>
        </div>

        <Card className="p-8 mt-6 bg-red-50 border-red-200">
          <div className="flex flex-col items-center text-center gap-4">
            <AlertCircle className="w-12 h-12 text-red-600" />
            <div>
              <h2 className="text-xl font-semibold text-red-900 mb-2">{t('incentives.unable_to_load_milestones', 'Unable to Load Milestones')}</h2>
              <p className="text-red-800 mb-4">{error}</p>
              <div className="space-y-2 text-sm text-red-700">
                <p><strong>{t('incentives.possible_solutions', 'Possible solutions:')}</strong></p>
                <ul className="list-disc list-inside text-left inline-block">
                  <li>{t('incentives.ensure_the_backend_server_is', 'Ensure the backend server is running')}</li>
                  <li>{t('incentives.check_that_youre_logged_in', 'Check that you\'re logged in with a valid auth token')}</li>
                  <li>{t('incentives.verify_that_the_memberactivitymilestones_table', 'Verify that the member_activity_milestones table has been created')}</li>
                  <li>{t('incentives.check_that_milestone_data_has', 'Check that milestone data has been seeded in the database')}</li>
                </ul>
              </div>
            </div>
            <Button
              onClick={fetchAvailableMilestones}
              className="mt-4"
            >
              {t('incentives.retry_loading_milestones', 'Retry Loading Milestones')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        {!tabbed && (
        <h1 className="text-3xl font-bold text-gray-900">{t('incentives.configure_program_milestones_1', 'Configure Program Milestones')}</h1>
        )}
        <p className="text-sm text-gray-600 mt-2">
          {t('incentives.create_a_new_program_with_1', 'Create a new program with sponsor-configurable milestone payouts')}
        </p>
      </div>

      {/* Guidance Section */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Target className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">{t('incentives.about_program_milestone_configuration', 'About Program Milestone Configuration')}</h3>
            <p className="text-sm text-blue-800 mb-2">
              This page allows sponsors to create new programs with customized milestone payouts.
              Configure how much CBOs and members earn when milestones are completed.
            </p>
            <div className="text-sm text-blue-700">
              <strong>{t('incentives.how_to_use', 'How to use:')}</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>{t('incentives.first_create_milestone_types_in', 'First, create milestone types in "Manage Milestone Types" if none exist')}</li>
                <li>Fill in program details (name, dates, description)</li>
                <li>{t('incentives.add_milestones_and_configure_payout', 'Add milestones and configure payout amounts for each')}</li>
                <li>{t('incentives.set_target_quantities_and_timeframes', 'Set target quantities and timeframes for milestone completion')}</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* Warning when no milestones are available */}
      {availableMilestones.length === 0 && !error && (
        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-start gap-3 text-yellow-800">
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-medium block mb-1">{t('incentives.no_milestones_available', 'No milestones available')}</span>
              <span className="text-sm block mb-3">
                {t('incentives.you_need_to_create_milestone', 'You need to create milestone definitions first before configuring programs.')}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCreateMilestoneDialog(true)}
                className="flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {t('incentives.create_first_milestone', 'Create First Milestone')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Success Alert */}
      {success && (
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{t('incentives.program_created_successfully', 'Program created successfully!')}</span>
          </div>
        </Card>
      )}

      {/* Error Alert */}
      {error && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Program Details */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('incentives.program_details', 'Program Details')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="program_name">{t('incentives.program_name', 'Program Name *')}</Label>
              <Input
                id="program_name"
                type="text"
                value={programData.name}
                onChange={(e) => setProgramData({ ...programData, name: e.target.value })}
                placeholder="e.g., Diabetes Management Program"
                required
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="program_description">{t('incentives.description', 'Description')}</Label>
              <Textarea
                id="program_description"
                value={programData.description}
                onChange={(e) => setProgramData({ ...programData, description: e.target.value })}
                placeholder={t('incentives.describe_the_program_and_its', 'Describe the program and its goals')}
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="start_date">{t('incentives.start_date', 'Start Date *')}</Label>
              <Input
                id="start_date"
                type="date"
                value={programData.start_date}
                onChange={(e) => setProgramData({ ...programData, start_date: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="end_date">{t('incentives.end_date', 'End Date *')}</Label>
              <Input
                id="end_date"
                type="date"
                value={programData.end_date}
                onChange={(e) => setProgramData({ ...programData, end_date: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="enrollment_limit">Enrollment Limit (Optional)</Label>
              <Input
                id="enrollment_limit"
                type="number"
                value={programData.enrollment_limit || ''}
                onChange={(e) => setProgramData({
                  ...programData,
                  enrollment_limit: e.target.value ? parseInt(e.target.value) : undefined
                })}
                placeholder={t('incentives.leave_empty_for_unlimited', 'Leave empty for unlimited')}
              />
            </div>
          </div>
        </Card>

        {/* Milestones Configuration */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('incentives.milestone_configuration', 'Milestone Configuration')}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {t('incentives.configure_payouts_and_targets_for', 'Configure payouts and targets for each milestone')}
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="button" onClick={addMilestone} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                {t('incentives.add_milestone', 'Add Milestone')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateMilestoneDialog(true)}
                className="flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {t('incentives.create_new_type', 'Create New Type')}
              </Button>
            </div>
          </div>

          {milestones.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">{t('incentives.no_milestones_configured_yet', 'No milestones configured yet')}</p>
              <Button type="button" onClick={addMilestone} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                {t('incentives.add_first_milestone', 'Add First Milestone')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {milestones.map((milestone, index) => (
                <Card key={index} className="p-4 bg-gray-50">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Milestone {index + 1}
                    </h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeMilestone(index)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Milestone Selection */}
                    <div className="md:col-span-3">
                      <Label>{t('incentives.milestone_type', 'Milestone Type *')}</Label>
                      <Select
                        value={milestone.milestone_id?.toString() || ''}
                        onValueChange={(value) => updateMilestone(index, 'milestone_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('incentives.select_a_milestone', 'Select a milestone')} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableMilestones.map((m) => (
                            <SelectItem key={m.id} value={m.id.toString()}>
                              {m.milestone_name} ({m.activity_type})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Target Configuration */}
                    <div>
                      <Label>{t('incentives.target_quantity', 'Target Quantity *')}</Label>
                      <Input
                        type="number"
                        min="1"
                        value={milestone.target_quantity}
                        onChange={(e) => updateMilestone(index, 'target_quantity', parseInt(e.target.value) || 1)}
                      />
                    </div>

                    <div>
                      <Label>{t('incentives.timeframe_days', 'Timeframe (Days)')}</Label>
                      <Input
                        type="number"
                        min="1"
                        value={milestone.target_timeframe_days || ''}
                        onChange={(e) => updateMilestone(index, 'target_timeframe_days', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder={t('incentives.optional', 'Optional')}
                      />
                    </div>

                    <div className="flex items-center gap-2 pt-6">
                      <input
                        type="checkbox"
                        id={`required_${index}`}
                        checked={milestone.is_required}
                        onChange={(e) => updateMilestone(index, 'is_required', e.target.checked)}
                        className="rounded"
                      />
                      <Label htmlFor={`required_${index}`} className="mb-0">
                        {t('incentives.required_milestone', 'Required Milestone')}
                      </Label>
                    </div>

                    {/* Payout Configuration */}
                    <div className="md:col-span-3 border-t pt-4 mt-2">
                      <div className="flex items-center gap-2 mb-3">
                        <CreditCard className="w-4 h-4 text-green-600" />
                        <h4 className="font-semibold text-gray-900">{t('incentives.payout_configuration', 'Payout Configuration')}</h4>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>{t('incentives.cbo_payout_amount', 'CBO Payout Amount')}</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={milestone.cbo_payout_amount}
                              onChange={(e) => updateMilestone(index, 'cbo_payout_amount', parseFloat(e.target.value) || 0)}
                              className="pl-7"
                            />
                          </div>
                        </div>

                        <div>
                          <Label>{t('incentives.member_payout_amount', 'Member Payout Amount')}</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={milestone.member_payout_amount}
                              onChange={(e) => updateMilestone(index, 'member_payout_amount', parseFloat(e.target.value) || 0)}
                              className="pl-7"
                            />
                          </div>
                        </div>

                        <div>
                          <Label>{t('incentives.points_awarded', 'Points Awarded')}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={milestone.points_awarded}
                            onChange={(e) => updateMilestone(index, 'points_awarded', parseInt(e.target.value) || 0)}
                          />
                        </div>

                        <div>
                          <Label>{t('incentives.max_payouts_per_member', 'Max Payouts Per Member')}</Label>
                          <Input
                            type="number"
                            min="1"
                            value={milestone.max_payouts_per_member || ''}
                            onChange={(e) => updateMilestone(index, 'max_payouts_per_member', e.target.value ? parseInt(e.target.value) : undefined)}
                            placeholder={t('incentives.unlimited', 'Unlimited')}
                          />
                        </div>
                      </div>

                      {/* Payout Summary */}
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-blue-700">{t('incentives.total_payout_per_completion', 'Total Payout per Completion:')}</span>
                          <span className="text-lg font-bold text-blue-900">
                            ${(milestone.cbo_payout_amount + milestone.member_payout_amount).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>

        {/* Summary */}
        {milestones.length > 0 && (
          <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <h3 className="text-lg font-semibold text-purple-900 mb-4">{t('incentives.program_summary', 'Program Summary')}</h3>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-700">{t('incentives.total_milestones', 'Total Milestones')}</span>
                </div>
                <p className="text-2xl font-bold text-purple-900">{milestones.length}</p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-700">{t('incentives.required_milestones', 'Required Milestones')}</span>
                </div>
                <p className="text-2xl font-bold text-purple-900">
                  {milestones.filter(m => m.is_required).length}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-700">{t('incentives.total_points', 'Total Points')}</span>
                </div>
                <p className="text-2xl font-bold text-purple-900">
                  {milestones.reduce((sum, m) => sum + m.points_awarded, 0)}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-700">{t('incentives.total_payout_value', 'Total Payout Value')}</span>
                </div>
                <p className="text-2xl font-bold text-purple-900">
                  ${calculateTotalPayouts().toFixed(2)}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setProgramData({
                name: '',
                description: '',
                start_date: '',
                end_date: '',
                is_active: true
              });
              setMilestones([]);
              setError(null);
            }}
          >
            {t('incentives.reset', 'Reset')}
          </Button>
          <Button type="submit" disabled={loading || milestones.length === 0}>
            {loading ? (
              <>
                <LoadingSpinner />
                <span className="ml-2">{t('incentives.creating_program', 'Creating Program...')}</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('incentives.create_program', 'Create Program')}
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Create New Milestone Dialog */}
      <Dialog open={showCreateMilestoneDialog} onOpenChange={setShowCreateMilestoneDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('incentives.create_new_milestone_type', 'Create New Milestone Type')}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateMilestone} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="new_activity_type">{t('incentives.activity_type', 'Activity Type *')}</Label>
              <Select
                value={newMilestoneForm.activity_type}
                onValueChange={(value) => setNewMilestoneForm({ ...newMilestoneForm, activity_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('incentives.select_activity_type', 'Select activity type')} />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="new_milestone_name">{t('incentives.milestone_name', 'Milestone Name *')}</Label>
              <Input
                id="new_milestone_name"
                value={newMilestoneForm.milestone_name}
                onChange={(e) => setNewMilestoneForm({ ...newMilestoneForm, milestone_name: e.target.value })}
                placeholder="e.g., enrollment_approved"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Use lowercase with underscores (e.g., enrollment_approved)
              </p>
            </div>

            <div>
              <Label htmlFor="new_milestone_description">{t('incentives.description_1', 'Description')}</Label>
              <Textarea
                id="new_milestone_description"
                value={newMilestoneForm.milestone_description}
                onChange={(e) => setNewMilestoneForm({ ...newMilestoneForm, milestone_description: e.target.value })}
                placeholder={t('incentives.describe_what_this_milestone_represents', 'Describe what this milestone represents')}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new_points">{t('incentives.default_points', 'Default Points')}</Label>
                <Input
                  id="new_points"
                  type="number"
                  min="0"
                  value={newMilestoneForm.points_awarded}
                  onChange={(e) => setNewMilestoneForm({ ...newMilestoneForm, points_awarded: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label htmlFor="new_monetary">Default Monetary Value ($)</Label>
                <Input
                  id="new_monetary"
                  type="number"
                  min="0"
                  step="0.01"
                  value={newMilestoneForm.monetary_value}
                  onChange={(e) => setNewMilestoneForm({ ...newMilestoneForm, monetary_value: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="new_is_repeatable"
                checked={newMilestoneForm.is_repeatable}
                onChange={(e) => setNewMilestoneForm({ ...newMilestoneForm, is_repeatable: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="new_is_repeatable" className="cursor-pointer">
                Repeatable (can be earned multiple times)
              </Label>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateMilestoneDialog(false);
                  setNewMilestoneForm({
                    activity_type: '',
                    milestone_name: '',
                    milestone_description: '',
                    points_awarded: 0,
                    monetary_value: 0,
                    is_repeatable: false,
                    is_active: true
                  });
                }}
              >
                {t('incentives.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={createMilestoneLoading}>
                {createMilestoneLoading ? 'Creating...' : 'Create Milestone'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramMilestoneConfiguration;
